import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  RemoveCircleOutline as RemoveLineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { BankAccountPicker, LedgerPicker, SupplierPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ApStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ApShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const emptyLine = () => ({ supplier_id: '', invoice_id: '', amount: '', openBills: [], loadingBills: false });
const emptyForm = () => ({
  batch_date: new Date().toISOString().slice(0, 10), paid_from_account_id: '',
  lines: [emptyLine()],
});

// Payment batches: one bank debit funding several supplier payments at
// once (a "payment run") - a concept AR has no equivalent of. Field names
// have no AccountingClient.php equivalent to verify against; they follow
// this ledger's own DRAFT -> APPROVED -> POSTED convention and the same
// {customer_id/supplier_id, invoice_id, allocated_amount} shape the
// verified AR receipt payload uses, generalised to a list of lines.
const PayableBatchesTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.AP, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.AP, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.AP, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const setLine = (index, patch) => setValues((p) => ({
    ...p, lines: p.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)),
  }));

  const setLineSupplier = (index, supplierId) => {
    setLine(index, { supplier_id: supplierId, invoice_id: '', amount: '', openBills: [], loadingBills: !!supplierId });
    if (!supplierId) return;
    ledgerAPI.getPayableInvoices({ supplier_id: supplierId, limit: 100 })
      .then((res) => {
        const list = unwrapList(res).filter((inv) => Number(inv.balance_due) > 0);
        setLine(index, { openBills: list, loadingBills: false });
      })
      .catch(() => setLine(index, { openBills: [], loadingBills: false }));
  };

  const setLineInvoice = (index, invoiceId) => {
    setValues((p) => {
      const line = p.lines[index];
      const bill = line.openBills.find((b) => String(b.invoice_id) === String(invoiceId));
      return {
        ...p,
        lines: p.lines.map((l, i) => (i === index ? { ...l, invoice_id: invoiceId, amount: bill ? String(bill.balance_due) : l.amount } : l)),
      };
    });
  };

  const addLine = () => setValues((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (index) => setValues((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== index) }));

  const linesTotal = values.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const submitCreate = async () => {
    if (!values.paid_from_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    const lines = values.lines
      .filter((l) => l.supplier_id && l.invoice_id)
      .map((l) => ({ supplier_id: Number(l.supplier_id), invoice_id: Number(l.invoice_id), amount: Number(l.amount) }));
    if (lines.length < 1) {
      showSnackbar('Add at least one supplier + bill line', 'error');
      return;
    }
    if (lines.some((l) => !(l.amount > 0))) {
      showSnackbar('Each line needs an amount greater than zero', 'error');
      return;
    }
    const payload = {
      batch_date: values.batch_date,
      paid_from_account_id: Number(values.paid_from_account_id),
      lines,
    };
    try {
      setSaving(true);
      await ledgerAPI.createPayableBatch(payload);
      showSnackbar('Payment batch created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this payment batch?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Payment batch ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getPayableBatch(row.batch_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch payment batch'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidPayableBatch(voidDlg.item.batch_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Payment batch voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailLines = Array.isArray(detail?.lines) ? detail.lines : [];
  const detailTotal = detailLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.AP}
        title="Payment Batches"
        subtitle="Bundle several supplier payments into a single bank debit"
        addLabel="New Batch"
        idKey="batch_id"
        searchPlaceholder="Search batch #..."
        emptyMessage="No payment batches found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getPayableBatches(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Batch date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'total' },
        ]}
        columns={[
          { label: 'Batch #', render: (b) => <strong>{b.batch_number || `BATCH-${b.batch_id}`}</strong> },
          { label: 'Date', render: (b) => fmtDate(b.batch_date) },
          { label: 'Paid From', render: (b) => look.accountLabel(b.paid_from_account_id) },
          { label: 'Total', render: (b) => formatAccountingAmount(b.total_amount), align: 'right' },
          { label: 'Status', render: (b) => <ApStatusChip status={b.status} /> },
        ]}
        rowActions={(b) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(b) }];
          if (b.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approvePayableBatch(b.batch_id)) });
          }
          if (b.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postPayableBatch(b.batch_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(b.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: b, void_reason: '' }) });
          }
          if (b.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete payment batch',
                message: `Delete DRAFT batch ${b.batch_number || `BATCH-${b.batch_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deletePayableBatch(b.batch_id);
                    showSnackbar('Payment batch deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Payment Batch</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Batch Date" value={values.batch_date}
                onChange={(e) => setValues((p) => ({ ...p, batch_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <BankAccountPicker label="Paid From" value={values.paid_from_account_id} onChange={(id) => setValues((p) => ({ ...p, paid_from_account_id: id }))} required />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" gutterBottom>Payment lines</Typography>
          {values.lines.map((l, i) => (
            <Stack key={i} direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
                <SupplierPicker label={`Line ${i + 1} supplier`} value={l.supplier_id} onChange={(id) => setLineSupplier(i, id)} />
              </Box>
              <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                {!l.supplier_id ? (
                  <Typography variant="caption" color="text.secondary">Pick a supplier first</Typography>
                ) : l.loadingBills ? (
                  <Typography variant="caption" color="text.secondary">Loading open bills...</Typography>
                ) : l.openBills.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">No open bills</Typography>
                ) : (
                  <LedgerPicker
                    label="Bill"
                    value={l.invoice_id}
                    onChange={(id) => setLineInvoice(i, id)}
                    options={l.openBills.map((b) => ({ value: b.invoice_id, label: `${b.invoice_number || `BILL-${b.invoice_id}`} - Balance ${formatAccountingAmount(b.balance_due)}` }))}
                  />
                )}
              </Box>
              <TextField
                size="small" type="number" label="Amount" value={l.amount}
                onChange={(e) => setLine(i, { amount: e.target.value })}
                inputProps={{ min: 0.01, step: 'any' }} sx={{ width: 140 }}
              />
              <IconButton size="small" color="error" title="Remove line" onClick={() => removeLine(i)} disabled={values.lines.length <= 1}>
                <RemoveLineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
            Total: {formatAccountingAmount(linesTotal)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Batch {detail?.batch_number || (detail?.batch_id ? `BATCH-${detail.batch_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {fmtDate(detail.batch_date)} - {look.accountLabel(detail.paid_from_account_id)}
              </Typography>
              <DataTable
                columns={['Supplier', 'Bill', 'Amount']}
                data={detailLines}
                loading={false}
                emptyMessage="No lines."
                renderRow={(l, i) => (
                  <TableRow key={l.line_id ?? i}>
                    <TableCell>{l.supplier?.name || look.supplierLabel(l.supplier_id, `Supplier ${l.supplier_id}`)}</TableCell>
                    <TableCell>{l.invoice?.invoice_number || `BILL-${l.invoice_id}`}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(l.amount)}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Total: <strong>{formatAccountingAmount(detail.total_amount ?? detailTotal)}</strong></Typography>
                <ApStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void batch ${voidDlg.item?.batch_number || (voidDlg.item ? `BATCH-${voidDlg.item.batch_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default PayableBatchesTab;
